import React from 'react';
import { buildPageList } from '../../utils/buildPageList';
import { Button } from './index';

/**
 * Reusable Pagination component with:
 * - "Showing X-Y of Z" summary
 * - Rows per page selector (dropdown)
 * - Previous / Next buttons
 * - Page number buttons with ellipsis
 *
 * Props:
 * @param {number} currentPage - Current page (1-based)
 * @param {number} totalPages - Total number of pages
 * @param {number} totalItems - Total number of items
 * @param {number} pageSize - Current page size
 * @param {function} onPageChange - Callback when page changes
 * @param {function} onPageSizeChange - Callback when page size changes
 * @param {number[]} pageSizeOptions - Array of page size options (default [10, 25, 50, 100])
 * @param {boolean} showSummary - Whether to show "Showing X-Y of Z" text (default true)
 * @param {boolean} showPageSizeSelector - Whether to show rows-per-page dropdown (default true)
 * @param {string} pageSizeLabel - Label for the rows-per-page dropdown (default "Rows per page")
 * @param {string} className - Additional CSS classes for the outer container
 */
export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  showSummary = true,
  showPageSizeSelector = true,
  pageSizeLabel = 'Rows per page',
  className = '',
}) => {
  if (totalPages <= 0 && totalItems <= 0) return null;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const pageList = buildPageList(currentPage, Math.max(totalPages, 1));

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100 ${className}`}>
      {/* Summary */}
      {showSummary && (
        <span className="text-xs sm:text-sm text-gray-500">
          Showing {startItem}–{endItem} of {totalItems}
        </span>
      )}

      {/* Rows per page + Page navigation */}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {/* Rows per page selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <label className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">{pageSizeLabel}</label>
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
              }}
              className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 cursor-pointer"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}

        {/* Page info (mobile) */}
        <span className="text-xs sm:text-sm text-gray-500 sm:hidden">
          Page {currentPage} of {totalPages}
        </span>

        {/* Page navigation */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </Button>

          {/* Page number buttons (hidden on very small screens, shown on sm+) */}
          <div className="hidden sm:flex items-center gap-1">
            {pageList.map((item, idx) =>
              item === '...' ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-2 text-sm text-gray-400 select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => onPageChange(item)}
                  className={
                    currentPage === item
                      ? 'min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors'
                      : 'min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors'
                  }
                >
                  {item}
                </button>
              )
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;